<template>
  <div class="blog-wrapper">

    <b-row class="blog-list-wrapper">
      <!-- Search + Filters -->
      <b-card no-body class="col-md-12">
        <b-card-header class="pb-50">
          <div class="d-flex justify-content-between align-items-center w-100">
            <h5 class="mb-0">
              Артисты<span v-if="total !== null" class="text-muted font-small-3 ml-50">({{ total }})</span>
            </h5>
            <div>
              <b-button
                  variant="outline-secondary"
                  size="sm"
                  v-b-toggle.filters-collapse
                  class="mr-50"
              >
                <feather-icon icon="FilterIcon" class="mr-25" />
                Фильтры
              </b-button>
              <b-button
                  v-if="hasActiveFilters"
                  variant="outline-danger"
                  size="sm"
                  @click="resetFilters"
              >
                <feather-icon icon="XIcon" class="mr-25" />
                Сбросить
              </b-button>
            </div>
          </div>
        </b-card-header>
        <b-card-body>
          <!-- Search -->
          <b-row>
            <b-col cols="12" class="mb-1">
              <b-input-group>
                <b-form-input
                    v-model="searchQuery"
                    placeholder="Поиск по названию артиста..."
                    @keyup.enter="applyFilters()"
                />
                <b-input-group-append>
                  <b-button variant="primary" @click="applyFilters()">
                    Применить
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-col>
          </b-row>

          <!-- Quick presets -->
          <b-row class="mb-1">
            <b-col cols="12">
              <b-button
                  v-for="preset in presets"
                  :key="preset.label"
                  :variant="isPresetActive(preset) ? 'primary' : 'outline-primary'"
                  size="sm"
                  class="mr-50 mb-50"
                  @click="applyPreset(preset)"
              >
                {{ preset.label }}
              </b-button>
            </b-col>
          </b-row>

          <!-- Collapsible Filters -->
          <b-collapse id="filters-collapse" v-model="filtersVisible">
            <!-- Section 1: Признаки -->
            <b-card no-body class="mb-1">
              <b-card-header header-tag="header" class="p-1" role="tab">
                <b-button block v-b-toggle.section-flags variant="light" class="text-left">
                  Признаки
                </b-button>
              </b-card-header>
              <b-collapse id="section-flags" visible accordion="filters-accordion" role="tabpanel">
                <b-card-body>
                  <!-- Boolean flags -->
                  <b-row class="mb-1">
                    <b-col
                        v-for="flag in booleanFlags"
                        :key="flag.key"
                        cols="6"
                        md="4"
                        lg="3"
                        class="mb-75"
                    >
                      <label class="d-block font-small-3 text-muted mb-25">{{ flag.label }}</label>
                      <b-form-radio-group
                          v-model="filters[flag.key]"
                          :options="flag.options"
                          buttons
                          button-variant="outline-primary"
                          size="sm"
                          @change="applyFilters()"
                      />
                    </b-col>
                  </b-row>

                  <!-- Difficulty range (visible only when has_difficulty=1) -->
                  <b-row v-if="filters.has_difficulty === '1'" class="mb-1">
                    <b-col cols="6" md="4" lg="3">
                      <label class="d-block font-small-3 text-muted mb-25">Сложность (0–10)</label>
                      <div class="d-flex align-items-center">
                        <b-form-input
                            v-model="filters.difficulty_rating_min"
                            type="number"
                            min="0"
                            max="10"
                            placeholder="от"
                            size="sm"
                            class="mr-25"
                            style="max-width: 80px"
                            @change="applyFilters()"
                        />
                        <span class="text-muted">–</span>
                        <b-form-input
                            v-model="filters.difficulty_rating_max"
                            type="number"
                            min="0"
                            max="10"
                            placeholder="до"
                            size="sm"
                            class="ml-25"
                            style="max-width: 80px"
                            @change="applyFilters()"
                        />
                      </div>
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-collapse>
            </b-card>

            <!-- Section 2: Наличие на платформах -->
            <b-card no-body class="mb-1">
              <b-card-header header-tag="header" class="p-1" role="tab">
                <b-button block v-b-toggle.section-platforms variant="light" class="text-left">
                  Наличие на платформах
                </b-button>
              </b-card-header>
              <b-collapse id="section-platforms" accordion="filters-accordion" role="tabpanel">
                <b-card-body>
                  <b-row class="mt-50">
                    <b-col
                        v-for="flag in externalFlags"
                        :key="flag.key"
                        cols="6"
                        md="4"
                        lg="3"
                        class="mb-75"
                    >
                      <label class="d-block font-small-3 text-muted mb-25">{{ flag.label }}</label>
                      <b-form-radio-group
                          v-model="filters[flag.key]"
                          :options="triStateOptions"
                          buttons
                          button-variant="outline-primary"
                          size="sm"
                          @change="applyFilters()"
                      />
                    </b-col>
                    <b-col
                        v-for="flag in syncFlags"
                        :key="flag.key"
                        cols="6"
                        md="4"
                        lg="3"
                        class="mb-75"
                    >
                      <label class="d-block font-small-3 text-muted mb-25">{{ flag.label }}</label>
                      <b-form-radio-group
                          v-model="filters[flag.key]"
                          :options="triStateOptions"
                          buttons
                          button-variant="outline-primary"
                          size="sm"
                          @change="applyFilters()"
                      />
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-collapse>
            </b-card>

            <!-- Section 3: Числовые диапазоны -->
            <b-card no-body class="mb-1">
              <b-card-header header-tag="header" class="p-1" role="tab">
                <b-button block v-b-toggle.section-ranges variant="light" class="text-left">
                  Числовые диапазоны
                </b-button>
              </b-card-header>
              <b-collapse id="section-ranges" accordion="filters-accordion" role="tabpanel">
                <b-card-body>
                  <b-row>
                    <b-col
                        v-for="range in numericRanges"
                        :key="range.key"
                        cols="6"
                        md="4"
                        lg="3"
                        class="mb-75"
                    >
                      <label class="d-block font-small-3 text-muted mb-25">{{ range.label }}</label>
                      <div class="d-flex align-items-center">
                        <b-form-input
                            v-model="filters[range.key + '_min']"
                            type="number"
                            placeholder="от"
                            size="sm"
                            class="mr-25"
                            style="max-width: 80px"
                            @change="applyFilters()"
                        />
                        <span class="text-muted">–</span>
                        <b-form-input
                            v-model="filters[range.key + '_max']"
                            type="number"
                            placeholder="до"
                            size="sm"
                            class="ml-25"
                            style="max-width: 80px"
                            @change="applyFilters()"
                        />
                      </div>
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-collapse>
            </b-card>

            <!-- Section 4: Категоризация -->
            <b-card no-body class="mb-1">
              <b-card-header header-tag="header" class="p-1" role="tab">
                <b-button block v-b-toggle.section-categorization variant="light" class="text-left">
                  Категоризация
                </b-button>
              </b-card-header>
              <b-collapse id="section-categorization" accordion="filters-accordion" role="tabpanel">
                <b-card-body>
                  <b-row class="mb-1">
                    <b-col cols="6" md="4" lg="3">
                      <label class="d-block font-small-3 text-muted mb-25">Кластер</label>
                      <v-select
                          v-model="filters.cluster"
                          :options="clusterOptions"
                          :reduce="o => o.value"
                          :clearable="true"
                          placeholder="Все"
                          @input="applyFilters()"
                      />
                    </b-col>
                    <b-col cols="6" md="4" lg="3">
                      <label class="d-block font-small-3 text-muted mb-25">Список</label>
                      <b-form-input
                          v-model="filters.list_id"
                          type="number"
                          placeholder="ID списка"
                          size="sm"
                          @change="applyFilters()"
                      />
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-collapse>
            </b-card>
          </b-collapse>
        </b-card-body>
      </b-card>

      <!-- Sort controls -->
      <b-card no-body class="col-md-12 mb-1">
        <b-card-body class="py-75">
          <b-row class="align-items-center">
            <b-col cols="auto">
              <div class="d-flex align-items-center">
                <label class="text-nowrap mb-0 mr-50 font-small-3">Сортировка:</label>
                <v-select
                    v-model="sortBy"
                    :options="sortOptions"
                    :reduce="o => o.value"
                    :clearable="false"
                    style="min-width: 220px"
                    @input="applyFilters()"
                />
                <b-button
                    variant="flat-primary"
                    class="btn-icon ml-50"
                    size="sm"
                    @click="toggleSortDir"
                >
                  <feather-icon :icon="isSortDirDesc ? 'ArrowDownIcon' : 'ArrowUpIcon'" />
                </b-button>
              </div>
            </b-col>
            <b-col cols="auto">
              <div class="d-flex align-items-center">
                <label class="text-nowrap mb-0 mr-50 font-small-3">На странице:</label>
                <v-select
                    v-model="perPage"
                    :options="perPageOptions"
                    :clearable="false"
                    style="min-width: 80px"
                    @input="applyFilters()"
                />
              </div>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <!-- Table -->
      <b-table
          ref="refArtistsListTable"
          :items="artistsList"
          responsive
          :fields="tableColumns"
          :tbody-tr-class="rowClass"
          primary-key="id"
          show-empty
          empty-text="Ничего не найдено"
          class="position-relative"
          :sort-by.sync="sortBy"
          :sort-desc.sync="isSortDirDesc"
          no-local-sorting
          :busy="loading"
      >

        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-50" />
            <strong>Загрузка...</strong>
          </div>
        </template>

        <template #cell(artist)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                  size="32"
                  :src="data.item.posterUrlPreview"
                  :text="avatarText(data.item.title)"
                  variant="primary"
              />
            </template>
            <span class="d-block" :title="data.item.title">
              <b-link
                  :to="{ name: 'artist-view', params: { id: data.item.id } }"
                  class="font-weight-bold"
              >
                {{ truncateString(data.item.title, 30) }}
              </b-link>
              <b-badge v-if="data.item.russian" variant="light-primary" class="font-small-2 ml-25">RU</b-badge>
              <b-badge v-if="data.item.is_cover_artist === 1" variant="light-warning" class="font-small-2 ml-25">Cover</b-badge>
            </span>
          </b-media>
        </template>

        <template #cell(questions)="data">
          <div class="d-inline-flex align-items-center questions-cell" style="gap: 0.85rem;">
            <!-- Треки: скачанные, красным если 0 -->
            <span
                class="d-inline-flex align-items-center"
                :class="data.item.downloaded_tracks_count ? '' : 'text-danger'"
                v-b-tooltip.hover
                title="Скачанные треки"
            >
              <feather-icon icon="MusicIcon" size="16" class="mr-25" />
              {{ data.item.downloaded_tracks_count || 0 }}
            </span>

            <!-- Обложки -->
            <span
                class="d-inline-flex align-items-center"
                :class="data.item.covers_count ? '' : 'text-muted'"
                v-b-tooltip.hover
                title="Обложки"
            >
              <feather-icon icon="DiscIcon" size="16" class="mr-25" />
              {{ data.item.covers_count || 0 }}
            </span>

            <!-- Составы: да/нет -->
            <span
                class="d-inline-flex align-items-center"
                v-b-tooltip.hover
                :title="data.item.members_count ? 'Состав есть' : 'Состава нет'"
            >
              <feather-icon icon="UsersIcon" size="16" class="mr-25" />
              <feather-icon
                  :icon="data.item.members_count ? 'CheckIcon' : 'XIcon'"
                  size="14"
                  :class="data.item.members_count ? 'text-success' : 'text-muted'"
              />
            </span>

            <!-- Фото -->
            <span
                class="d-inline-flex align-items-center"
                :class="data.item.photos_count ? '' : 'text-muted'"
                v-b-tooltip.hover
                title="Фото"
            >
              <feather-icon icon="CameraIcon" size="16" class="mr-25" />
              {{ data.item.photos_count || 0 }}
            </span>
          </div>
        </template>

        <template #head(difficulty_rating)="data">
          {{ data.label }}
          <feather-icon
              icon="HelpCircleIcon"
              size="14"
              class="text-muted ml-25"
              v-b-tooltip.hover.html="difficultyTooltip"
          />
        </template>

        <template #cell(difficulty_rating)="data">
          <div class="d-inline-flex align-items-center" style="gap: 0.75rem;">
            <b-form-rating
                :value="data.item.difficulty_rating"
                :stars="10"
                inline
                no-border
                size="sm"
                variant="warning"
                class="p-0"
                @change="updateDifficultyRating(data.item, $event)"
            />
            <rating-stats-block
                compact
                :rating="data.item.difficulty_rating"
                :calculated="data.item.calculated_difficulty"
                :lastfm="data.item.popularity"
                :spotify="data.item.spotify_playcount"
                :yandex-monthly-listeners="data.item.yandex_monthly_listeners"
            />
          </div>
        </template>

        <template #cell(external_ids)="data">
          <span class="text-nowrap d-inline-flex align-items-center" style="gap: 0.35rem;" v-if="data.item.external_ids">
            <a
                v-if="data.item.external_ids.second_hand_songs"
                :href="'https://secondhandsongs.com/artist/' + data.item.external_ids.second_hand_songs"
                target="_blank"
                rel="noopener noreferrer"
                v-b-tooltip.hover
                title="Second Hand Songs"
                class="ext-id-link"
                style="background-color: rgba(0, 207, 232, 0.12); color: #00cfe8;"
            >
              <feather-icon icon="DiscIcon" size="16" />
            </a>
            <a
                v-if="data.item.external_ids.spotify"
                :href="'https://open.spotify.com/artist/' + data.item.external_ids.spotify"
                target="_blank"
                rel="noopener noreferrer"
                v-b-tooltip.hover
                title="Spotify"
                class="ext-id-link"
                style="background-color: #1db954; color: #fff;"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="currentColor" d="M17.9 10.9C14.7 9 9.35 8.8 6.3 9.75c-.5.15-1-.15-1.15-.6c-.15-.5.15-1 .6-1.15c3.55-1.05 9.4-.85 13.1 1.35c.45.25.6.85.35 1.3c-.25.35-.85.5-1.3.25m-.1 2.8c-.25.35-.7.5-1.05.25c-2.7-1.65-6.8-2.15-9.95-1.15c-.4.1-.85-.1-.95-.5s.1-.85.5-.95c3.65-1.1 8.15-.55 11.25 1.35c.3.15.45.65.2 1m-1.2 2.75c-.2.3-.55.4-.85.2c-2.35-1.45-5.3-1.75-8.8-.95c-.35.1-.65-.15-.75-.45c-.1-.35.15-.65.45-.75c3.8-.85 7.1-.5 9.7 1.1c.35.15.4.55.25.85M12 2A10 10 0 0 0 2 12a10 10 0 0 0 10 10a10 10 0 0 0 10-10A10 10 0 0 0 12 2"/>
              </svg>
            </a>
            <a
                v-if="data.item.external_ids.yandex"
                :href="'https://music.yandex.ru/artist/' + data.item.external_ids.yandex"
                target="_blank"
                rel="noopener noreferrer"
                v-b-tooltip.hover
                title="Yandex Music"
                class="ext-id-link badge-yandex"
            >
              <feather-icon icon="HeadphonesIcon" size="16" />
            </a>
            <a
                v-if="data.item.external_ids.youtube"
                :href="'https://www.youtube.com/channel/' + data.item.external_ids.youtube"
                target="_blank"
                rel="noopener noreferrer"
                v-b-tooltip.hover
                title="YouTube"
                class="ext-id-link"
                style="background-color: #ff0000; color: #fff;"
            >
              <feather-icon icon="YoutubeIcon" size="16" />
            </a>
            <a
                v-if="data.item.external_ids.wikipedia"
                :href="wikipediaUrl(data.item.external_ids.wikipedia)"
                target="_blank"
                rel="noopener noreferrer"
                v-b-tooltip.hover
                title="Wikipedia"
                class="ext-id-link"
                style="background-color: rgba(130, 134, 139, 0.12); color: #82868b;"
            >
              <feather-icon icon="BookOpenIcon" size="16" />
            </a>
            <a
                v-if="data.item.external_ids.musicbrainz"
                :href="'https://musicbrainz.org/artist/' + data.item.external_ids.musicbrainz"
                target="_blank"
                rel="noopener noreferrer"
                v-b-tooltip.hover
                title="MusicBrainz"
                class="ext-id-link"
                style="background-color: #ba478f; color: #fff;"
            >
              <feather-icon icon="DatabaseIcon" size="16" />
            </a>
          </span>
        </template>

        <template #cell(actions)="data">
          <div class="text-nowrap">
            <b-button
                :to="{name: 'artist-view', params: { id: data.item.id }}"
                :variant="data.item.has_downloaded_tracks ? 'primary' : 'danger'"
                class="btn-icon"
                size="sm"
            >
              <feather-icon icon="ArrowRightCircleIcon"/>
            </b-button>
          </div>
        </template>
      </b-table>

      <b-col cols="12">
        <div class="my-2">
          <b-pagination
              v-if="total > 0"
              v-model="page"
              :per-page="perPage"
              align="center"
              :total-rows="total"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </div>
      </b-col>
    </b-row>

  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BLink,
  BMedia,
  BAvatar,
  BCardBody,
  BBadge,
  BPagination,
  BCardHeader,
  BTable,
  BButton,
  BFormRating,
  BFormRadioGroup,
  BCollapse,
  BSpinner,
  VBToggle,
  VBTooltip,
} from 'bootstrap-vue'
import { avatarText, truncateString } from '@core/utils/filter'
import store from '@/store'
import { computed, onUnmounted, ref, watch } from '@vue/composition-api'
import musicStoreModule from '@/views/db-music/musicStoreModule'
import vSelect from 'vue-select'
import RatingStatsBlock from '@/views/db-music/RatingStatsBlock.vue'

const BOOLEAN_FLAGS = [
  {
    key: 'russian',
    label: 'Русскоязычный',
    options: [
      { text: 'Все', value: null },
      { text: 'Русские', value: '1' },
      { text: 'Зарубежные', value: '0' },
    ],
  },
  {
    key: 'onehitwonder',
    label: 'One Hit Wonder',
    options: [
      { text: 'Все', value: null },
      { text: 'Только OHW', value: '1' },
      { text: 'Без OHW', value: '0' },
    ],
  },
  {
    key: 'has_downloaded_tracks',
    label: 'Скачанные треки',
    options: [
      { text: 'Все', value: null },
      { text: 'Есть', value: '1' },
      { text: 'Нет', value: '0' },
    ],
  },
  {
    key: 'is_cover_artist',
    label: 'Кавер-артист',
    options: [
      { text: 'Все', value: null },
      { text: 'Кавер', value: '1' },
      { text: 'Не кавер', value: '0' },
    ],
  },
  {
    key: 'notion',
    label: 'Notion',
    options: [
      { text: 'Все', value: null },
      { text: 'Да', value: '1' },
      { text: 'Нет', value: '0' },
    ],
  },
  {
    key: 'tags_only',
    label: 'Только для тегов',
    options: [
      { text: 'Все', value: null },
      { text: 'Да', value: '1' },
      { text: 'Нет', value: '0' },
    ],
  },
  {
    key: 'skip_originals',
    label: 'Пропуск оригиналов',
    options: [
      { text: 'Все', value: null },
      { text: 'Да', value: '1' },
      { text: 'Нет', value: '0' },
    ],
  },
  {
    key: 'has_difficulty',
    label: 'Сложность указана',
    options: [
      { text: 'Все', value: null },
      { text: 'Указана', value: '1' },
      { text: 'Не указана', value: '0' },
    ],
  },
]

const EXTERNAL_FLAGS = [
  { key: 'has_spotify', label: 'Spotify' },
  { key: 'has_yandex', label: 'Yandex Music' },
  { key: 'has_youtube', label: 'YouTube' },
  { key: 'has_shs', label: 'SecondHandSongs' },
  { key: 'has_wikipedia', label: 'Wikipedia' },
  { key: 'has_musicbrainz', label: 'MusicBrainz' },
  { key: 'has_covers', label: 'Обложки' },
]

const NUMERIC_RANGES = [
  { key: 'popularity', label: 'Популярность' },
  { key: 'tracks_count', label: 'Треки' },
  { key: 'downloaded_tracks_count', label: 'Скачанные треки' },
  { key: 'lists_count', label: 'Списки' },
  { key: 'spotify_playcount', label: 'Прослушивания Spotify' },
  { key: 'yandex_monthly_listeners', label: 'Слушатели Яндекс.Музыки за месяц' },
  { key: 'active_tracks_count', label: 'Активные треки' },
  { key: 'covers_count', label: 'Обложки' },
  { key: 'photos_count', label: 'Фото' },
]

const SYNC_FLAGS = [
  { key: 'synced_spotify', label: 'Синхронизирован со Spotify' },
  { key: 'synced_yandex', label: 'Синхронизирован с Яндексом' },
]

const CLUSTER_OPTIONS = [
  { label: 'Рок', value: 1 },
  { label: 'Поп', value: 2 },
  { label: 'Фолк', value: 3 },
  { label: 'Метал', value: 4 },
  { label: 'Русский Рок', value: 5 },
  { label: 'Ничего', value: 6 },
  { label: 'Рэп', value: 7 },
  { label: 'Электроника', value: 8 },
  { label: 'Шансон', value: 9 },
  { label: 'Советские фильмы', value: 10 },
  { label: 'Инди', value: 11 },
]

const SORT_OPTIONS = [
  { label: 'По названию', value: 'title' },
  { label: 'По популярности', value: 'popularity' },
  { label: 'По сложности', value: 'difficulty_rating' },
  { label: 'По дате добавления', value: 'created_at' },
  { label: 'По дате обновления', value: 'updated_at' },
  { label: 'По кол-ву треков', value: 'tracks_count' },
  { label: 'По кол-ву скачанных', value: 'downloaded_tracks_count' },
  { label: 'По кол-ву списков', value: 'lists_count' },
  { label: 'По прослушиваниям Spotify', value: 'spotify_playcount' },
  { label: 'По слушателям Яндекс.Музыки за месяц', value: 'yandex_monthly_listeners' },
]

const TRI_STATE_OPTIONS = [
  { text: 'Все', value: null },
  { text: 'Да', value: '1' },
  { text: 'Нет', value: '0' },
]

const PRESETS = [
  { label: 'Без треков', filters: { has_downloaded_tracks: '0' } },
  { label: 'Без Spotify', filters: { has_spotify: '0' } },
  { label: 'Русские', filters: { russian: '1' } },
  { label: 'Кавер-артисты', filters: { is_cover_artist: '1' } },
  { label: 'Без сложности', filters: { has_difficulty: '0' } },
]

function getDefaultFilters() {
  return {
    russian: null,
    onehitwonder: null,
    has_downloaded_tracks: null,
    is_cover_artist: null,
    notion: null,
    tags_only: null,
    skip_originals: null,
    has_difficulty: null,
    cluster: null,
    list_id: null,
    popularity_min: null,
    popularity_max: null,
    difficulty_rating_min: null,
    difficulty_rating_max: null,
    tracks_count_min: null,
    tracks_count_max: null,
    downloaded_tracks_count_min: null,
    downloaded_tracks_count_max: null,
    lists_count_min: null,
    lists_count_max: null,
    has_spotify: null,
    has_yandex: null,
    has_youtube: null,
    has_shs: null,
    has_wikipedia: null,
    has_musicbrainz: null,
    has_covers: null,
    spotify_playcount_min: null,
    spotify_playcount_max: null,
    yandex_monthly_listeners_min: null,
    yandex_monthly_listeners_max: null,
    active_tracks_count_min: null,
    active_tracks_count_max: null,
    covers_count_min: null,
    covers_count_max: null,
    photos_count_min: null,
    photos_count_max: null,
    synced_spotify: null,
    synced_yandex: null,
  }
}

// All filter keys that we sync to the URL
const FILTER_KEYS = Object.keys(getDefaultFilters())

export default {
  components: {
    RatingStatsBlock,
    BRow,
    BCol,
    BCard,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BLink,
    BCardBody,
    BMedia,
    BAvatar,
    BBadge,
    BPagination,
    BCardHeader,
    BTable,
    BButton,
    BFormRating,
    BFormRadioGroup,
    BCollapse,
    BSpinner,
    vSelect,
  },
  directives: {
    'b-toggle': VBToggle,
    'b-tooltip': VBTooltip,
  },
  methods: {
    rowClass(item, type) {
      if (!item || type !== 'row') return
      if (!item.has_downloaded_tracks) return 'table-danger'
    },
  },
  setup(props, { root }) {
    const STORE_MODULE_NAME = 'artists'
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    // --- State ---
    const page = ref(1)
    const perPage = ref(20)
    const searchQuery = ref(null)
    const total = ref(null)
    const sortBy = ref('popularity')
    const isSortDirDesc = ref(true)
    const loading = ref(false)
    const filtersVisible = ref(false)
    const filters = ref(getDefaultFilters())
    const artistsList = ref([])

    const perPageOptions = [20, 30, 50, 100]

    // --- Read URL query on init ---
    const route = root.$route
    if (route.query.q) searchQuery.value = route.query.q
    if (route.query.sort_by) sortBy.value = route.query.sort_by
    if (route.query.sort_desc !== undefined) isSortDirDesc.value = route.query.sort_desc === '1' || route.query.sort_desc === 'true'
    if (route.query.per_page) perPage.value = parseInt(route.query.per_page, 10) || 20
    if (route.query.page) page.value = parseInt(route.query.page, 10) || 1

    FILTER_KEYS.forEach(key => {
      if (route.query[key] !== undefined && route.query[key] !== '') {
        filters.value[key] = route.query[key]
      }
    })

    // Open filters panel if any filter is active from URL
    const hasActiveFilters = computed(() => {
      const f = filters.value
      return FILTER_KEYS.some(k => f[k] !== null && f[k] !== '' && f[k] !== undefined)
    })

    if (hasActiveFilters.value) {
      filtersVisible.value = true
    }

    // --- Table columns ---
    const tableColumns = [
      { key: 'id', label: '#', sortable: false },
      { key: 'artist', label: 'Артист', sortable: false },
      { key: 'popularity', label: 'Популярность', sortable: true },
      { key: 'questions', label: 'Вопросы', sortable: false },
      { key: 'difficulty_rating', label: 'Сложность', sortable: true, thClass: 'text-nowrap' },
      { key: 'external_ids', label: 'Внешние ID', sortable: false },
      { key: 'actions' },
    ]

    const difficultyTooltip = '<strong>1</strong> — Все знают, легко берется вокал<br>'
      + '<strong>2</strong> — Широко известна, узнаваемый вокал, заезженные хиты<br>'
      + '<strong>3</strong> — Широко известна в своем жанре, узнаваемый вокал, топ-хиты<br>'
      + '<strong>4</strong> — Вспоминаемая группа с топ-хитами<br>'
      + '<strong>5</strong> — Вспоминаемая группа с топ-хитом или несколькими, жанровая<br>'
      + '<strong>6</strong> — Вспоминаемая группа с хитом или несколькими<br>'
      + '<strong>7</strong> — Забытая группа с топ-хитом прошлого<br>'
      + '<strong>8</strong> — Малоизвестная группа, есть хит, но не топ<br>'
      + '<strong>9</strong> — Нишевая группа, где-то светилась<br>'
      + '<strong>10</strong> — Слабо известная группа, нишевая'

    // --- API ---
    const fetchArtists = (...args) => store.dispatch('artists/fetchArtists', ...args)
    const updateArtist = (id, queryParams) => store.dispatch('artists/updateArtist', { id, queryParams })

    const updateDifficultyRating = (artist, rating) => {
      updateArtist(artist.id, { difficulty_rating: rating })
        .then(() => {
          artist.difficulty_rating = rating
        })
    }

    const buildQueryParams = () => {
      const params = {
        q: searchQuery.value || undefined,
        page: page.value,
        per_page: perPage.value,
        sort_by: sortBy.value,
        sort_desc: isSortDirDesc.value,
      }
      const f = filters.value
      FILTER_KEYS.forEach(key => {
        if (f[key] !== null && f[key] !== '' && f[key] !== undefined) {
          // Не отправлять диапазон сложности, если has_difficulty != 1
          if ((key === 'difficulty_rating_min' || key === 'difficulty_rating_max') && f.has_difficulty !== '1') {
            return
          }
          params[key] = f[key]
        }
      })
      return params
    }

    const syncUrlQuery = () => {
      const params = buildQueryParams()
      // Clean undefined values
      const query = {}
      Object.keys(params).forEach(k => {
        if (params[k] !== undefined) {
          query[k] = String(params[k])
        }
      })
      // Avoid redundant navigation
      const currentQuery = root.$route.query
      const isSame = Object.keys(query).length === Object.keys(currentQuery).length
        && Object.keys(query).every(k => currentQuery[k] === query[k])
      if (!isSame) {
        root.$router.replace({ query }).catch(() => {})
      }
    }

    const fetchArtistsList = () => {
      const params = buildQueryParams()
      syncUrlQuery()
      loading.value = true
      fetchArtists(params)
        .then(response => {
          artistsList.value = response.data.data
          total.value = response.data.meta.total
        })
        .finally(() => {
          loading.value = false
        })
    }

    const applyFilters = () => {
      page.value = 1
      fetchArtistsList()
    }

    const resetFilters = () => {
      filters.value = getDefaultFilters()
      searchQuery.value = null
      applyFilters()
    }

    const toggleSortDir = () => {
      isSortDirDesc.value = !isSortDirDesc.value
      applyFilters()
    }

    const isPresetActive = (preset) => {
      const f = filters.value
      return Object.keys(preset.filters).every(k => f[k] === preset.filters[k])
    }

    const applyPreset = (preset) => {
      if (isPresetActive(preset)) {
        // Deactivate — reset preset keys to null
        Object.keys(preset.filters).forEach(k => {
          filters.value[k] = null
        })
      } else {
        Object.keys(preset.filters).forEach(k => {
          filters.value[k] = preset.filters[k]
        })
      }
      applyFilters()
    }

    const wikipediaUrl = (value) => {
      if (!value) return '#'
      if (/^https?:\/\//i.test(value)) return value
      const match = value.match(/^([a-z]{2,3})[:/](.+)$/i)
      if (match) return `https://${match[1]}.wikipedia.org/wiki/${match[2]}`
      return `https://ru.wikipedia.org/wiki/${value}`
    }

    // --- Watchers ---
    watch([page, sortBy, isSortDirDesc], () => {
      fetchArtistsList()
    })

    // Initial fetch
    fetchArtistsList()

    return {
      page,
      perPage,
      perPageOptions,
      searchQuery,
      total,
      sortBy,
      isSortDirDesc,
      loading,
      filtersVisible,
      filters,
      artistsList,
      tableColumns,
      difficultyTooltip,
      hasActiveFilters,

      // Constants
      booleanFlags: BOOLEAN_FLAGS,
      externalFlags: EXTERNAL_FLAGS,
      syncFlags: SYNC_FLAGS,
      numericRanges: NUMERIC_RANGES,
      clusterOptions: CLUSTER_OPTIONS,
      sortOptions: SORT_OPTIONS,
      triStateOptions: TRI_STATE_OPTIONS,
      presets: PRESETS,

      // Methods
      fetchArtistsList,
      applyFilters,
      resetFilters,
      toggleSortDir,
      isPresetActive,
      applyPreset,
      updateDifficultyRating,
      avatarText,
      truncateString,
      wikipediaUrl,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';

.ext-id-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 0.268rem;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.8;
    text-decoration: none;
  }
}
</style>
